import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  ReactNode,
  FC,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ENVS, getEnv } from '@adsk/acs-sc-web-platform-api';
import RemoteOverridePanel from '../components/RemoteOverridePanel';
import getOverridesFromQuery from '../utils/getOverridesFromQuery';
import safeParseObjectFromSessionStorage from '../utils/safeParseObjectFromSessionStorage';

const RemoteOverridesContext = createContext<Overrides>({});
export const useRemoteOverrides = () => useContext(RemoteOverridesContext);

export const OVERRIDE_SESSION_STORAEG_KEY = 'ACS_EXO_REMOTE_OVERRIDES';

const RemoteOverridesContainer = styled.div({
  top: 0,
  right: 0,
  position: 'fixed',
  // should be above top navigation
  zIndex: 1,
});

type Overrides = {
  [key: string]: string;
};

const RemoteOverridesProvider: FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const [overrides, setOverrides] = useState<Overrides>({});

  useEffect(() => {
    if (getEnv() !== ENVS.QA) return;

    const overridesFromQuery = getOverridesFromQuery();
    const overridesFromSessionStorage = safeParseObjectFromSessionStorage(
      OVERRIDE_SESSION_STORAEG_KEY
    );

    if (overridesFromSessionStorage && !overridesFromQuery) {
      setOverrides(overridesFromSessionStorage);
    } else if (overridesFromSessionStorage && overridesFromQuery) {
      const newOverrides = Object.assign(
        overridesFromSessionStorage,
        overridesFromQuery
      );
      setOverrides(newOverrides);
      sessionStorage.setItem(
        OVERRIDE_SESSION_STORAEG_KEY,
        JSON.stringify(newOverrides)
      );
    } else if (overridesFromQuery) {
      setOverrides(overridesFromQuery);
      sessionStorage.setItem(
        OVERRIDE_SESSION_STORAEG_KEY,
        JSON.stringify(overridesFromQuery)
      );
    }
  }, []);

  const resetRemoteToDefaultConfiguration = (scope: string) => {
    const overridesFromSessionStorage = safeParseObjectFromSessionStorage(
      OVERRIDE_SESSION_STORAEG_KEY
    );

    delete overridesFromSessionStorage[scope];

    sessionStorage.setItem(
      OVERRIDE_SESSION_STORAEG_KEY,
      JSON.stringify(overridesFromSessionStorage)
    );
    window.history.pushState({}, document.title, window.location.pathname);
    window.location.reload();
  };

  return (
    <RemoteOverridesContext.Provider value={overrides}>
      <RemoteOverridesContainer>
        {Object.keys(overrides).map((scope) => (
          <RemoteOverridePanel
            key={scope}
            scope={scope}
            url={overrides[scope]}
            onResetClick={() => resetRemoteToDefaultConfiguration(scope)}
          />
        ))}
      </RemoteOverridesContainer>
      {children}
    </RemoteOverridesContext.Provider>
  );
};

RemoteOverridesProvider.propTypes = {
  children: PropTypes.node,
};

export default RemoteOverridesProvider;
